// Система версионирования медицинских протоколов
export interface ProtocolVersion {
  major: number;
  minor: number;
  patch: number;
  build: string;
  fullVersion: string;
}

export interface MedicalProtocol {
  id: string;
  name: string;
  version: ProtocolVersion;
  data: any;
  sourceId: string;
  lastUpdated: Date;
  created: Date;
  checksum: string;
  changelog: ChangelogEntry[];
  isActive: boolean;
}

export interface ChangelogEntry {
  version: string;
  date: Date;
  type: 'major' | 'minor' | 'patch' | 'hotfix';
  changes: string[];
  author: string;
  description: string;
  breakingChanges?: string[];
}

export interface VersionHistory {
  protocolId: string;
  versions: string[];
  currentVersion: string;
  lastChecked: Date;
}

// Утилиты для работы с версиями
export const versionUtils = {
  // Создать версию из строки
  parseVersion: (versionString: string): ProtocolVersion => {
    const parts = versionString.split('.');
    const major = parseInt(parts[0]) || 0;
    const minor = parseInt(parts[1]) || 0;
    const patch = parseInt(parts[2]) || 0;
    const build = parts[3] || new Date().toISOString().split('T')[0].replace(/-/g, '');
    
    return {
      major,
      minor,
      patch,
      build,
      fullVersion: versionString
    };
  },

  // Создать строку версии
  createVersionString: (version: ProtocolVersion): string => {
    return `${version.major}.${version.minor}.${version.patch}.${version.build}`;
  },

  // Сравнить версии
  compareVersions: (v1: string, v2: string): number => {
    const version1 = versionUtils.parseVersion(v1);
    const version2 = versionUtils.parseVersion(v2);
    
    if (version1.major !== version2.major) {
      return version1.major - version2.major;
    }
    if (version1.minor !== version2.minor) {
      return version1.minor - version2.minor;
    }
    if (version1.patch !== version2.patch) {
      return version1.patch - version2.patch;
    }
    return version1.build.localeCompare(version2.build);
  },

  // Проверить, является ли версия новее
  isNewer: (newVersion: string, currentVersion: string): boolean => {
    return versionUtils.compareVersions(newVersion, currentVersion) > 0;
  },

  // Получить тип обновления
  getUpdateType: (oldVersion: string, newVersion: string): 'major' | 'minor' | 'patch' | 'hotfix' => {
    const old = versionUtils.parseVersion(oldVersion);
    const newV = versionUtils.parseVersion(newVersion);
    
    if (newV.major > old.major) return 'major';
    if (newV.minor > old.minor) return 'minor';
    if (newV.patch > old.patch) return 'patch';
    return 'hotfix';
  }
};

// Менеджер версий медицинских протоколов
export class MedicalVersionManager {
  private protocols: Map<string, MedicalProtocol> = new Map();
  private versionHistory: Map<string, VersionHistory> = new Map();

  // Добавить протокол
  addProtocol(protocol: MedicalProtocol): void {
    this.protocols.set(protocol.id, protocol);
    this.updateVersionHistory(protocol.id, protocol.version.fullVersion);
  }

  // Обновить протокол
  updateProtocol(protocolId: string, newData: any, changelog: ChangelogEntry): boolean {
    const protocol = this.protocols.get(protocolId);
    if (!protocol) return false;

    const newVersion = this.incrementVersion(protocol.version, changelog.type);
    
    const updatedProtocol: MedicalProtocol = {
      ...protocol,
      version: newVersion,
      data: newData,
      lastUpdated: new Date(),
      changelog: [...protocol.changelog, changelog]
    };

    this.protocols.set(protocolId, updatedProtocol);
    this.updateVersionHistory(protocolId, newVersion.fullVersion);
    return true;
  }

  // Получить протокол
  getProtocol(protocolId: string): MedicalProtocol | undefined {
    return this.protocols.get(protocolId);
  }

  // Получить все протоколы
  getAllProtocols(): MedicalProtocol[] {
    return Array.from(this.protocols.values());
  }

  // Получить активные протоколы
  getActiveProtocols(): MedicalProtocol[] {
    return Array.from(this.protocols.values()).filter(p => p.isActive);
  }

  // Получить историю версий
  getVersionHistory(protocolId: string): VersionHistory | undefined {
    return this.versionHistory.get(protocolId);
  }

  // Проверить наличие обновлений
  checkForUpdates(protocolId: string, currentVersion: string): boolean {
    const protocol = this.protocols.get(protocolId);
    if (!protocol) return false;
    
    return versionUtils.isNewer(protocol.version.fullVersion, currentVersion);
  }

  // Получить последнюю версию
  getLatestVersion(protocolId: string): string | null {
    const protocol = this.protocols.get(protocolId);
    return protocol ? protocol.version.fullVersion : null;
  }

  // Создать чексумму для данных
  createChecksum(data: any): string {
    const dataString = JSON.stringify(data, Object.keys(data).sort());
    let hash = 0;
    for (let i = 0; i < dataString.length; i++) {
      const char = dataString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  // Проверить целостность данных
  verifyIntegrity(protocolId: string): boolean {
    const protocol = this.protocols.get(protocolId);
    if (!protocol) return false;
    
    const currentChecksum = this.createChecksum(protocol.data);
    return currentChecksum === protocol.checksum;
  }

  // Увеличить версию
  private incrementVersion(currentVersion: ProtocolVersion, type: 'major' | 'minor' | 'patch' | 'hotfix'): ProtocolVersion {
    const newVersion = { ...currentVersion };
    
    switch (type) {
      case 'major':
        newVersion.major++;
        newVersion.minor = 0;
        newVersion.patch = 0;
        break;
      case 'minor':
        newVersion.minor++;
        newVersion.patch = 0;
        break;
      case 'patch':
        newVersion.patch++;
        break;
      case 'hotfix':
        newVersion.patch++;
        break;
    }
    
    newVersion.build = new Date().toISOString().split('T')[0].replace(/-/g, '');
    newVersion.fullVersion = versionUtils.createVersionString(newVersion);
    
    return newVersion;
  }

  // Обновить историю версий
  private updateVersionHistory(protocolId: string, version: string): void {
    const history = this.versionHistory.get(protocolId) || {
      protocolId,
      versions: [],
      currentVersion: version,
      lastChecked: new Date()
    };
    
    if (!history.versions.includes(version)) {
      history.versions.push(version);
      history.versions.sort((a, b) => versionUtils.compareVersions(b, a));
    }
    
    history.currentVersion = version;
    history.lastChecked = new Date();
    
    this.versionHistory.set(protocolId, history);
  }

  // Получить статистику версий
  getVersionStats(): {
    totalProtocols: number;
    totalVersions: number;
    outdatedProtocols: number;
    lastUpdate: Date | null;
  } {
    const protocols = Array.from(this.protocols.values());
    const now = new Date();
    const sixMonthsAgo = new Date(now.getTime() - 6 * 30 * 24 * 60 * 60 * 1000);
    
    let totalVersions = 0;
    let outdatedProtocols = 0;
    let lastUpdate: Date | null = null;
    
    protocols.forEach(protocol => {
      totalVersions += protocol.changelog.length;
      
      if (protocol.lastUpdated < sixMonthsAgo) {
        outdatedProtocols++;
      }
      
      if (!lastUpdate || protocol.lastUpdated > lastUpdate) {
        lastUpdate = protocol.lastUpdated;
      }
    });
    
    return {
      totalProtocols: protocols.length,
      totalVersions,
      outdatedProtocols,
      lastUpdate
    };
  }
}

// Создаем глобальный экземпляр менеджера версий
export const medicalVersionManager = new MedicalVersionManager();

// Инициализация базовых протоколов
export const initializeMedicalProtocols = (): void => {
  // Протокол вакцинации
  medicalVersionManager.addProtocol({
    id: 'vaccination-protocol',
    name: 'Протокол вакцинации детей',
    version: versionUtils.parseVersion('1.0.0.20241213'),
    data: {
      vaccines: [],
      schedule: {},
      contraindications: {}
    },
    sourceId: 'who-vaccination',
    lastUpdated: new Date('2024-12-13'),
    created: new Date('2024-12-13'),
    checksum: '',
    changelog: [{
      version: '1.0.0',
      date: new Date('2024-12-13'),
      type: 'major',
      changes: ['Первоначальная версия протокола вакцинации'],
      author: 'Система',
      description: 'Создание базового протокола вакцинации'
    }],
    isActive: true
  });

  // Протокол расчета доз
  medicalVersionManager.addProtocol({
    id: 'dosage-protocol',
    name: 'Протокол расчета дозировок',
    version: versionUtils.parseVersion('1.2.0.20241213'),
    data: {
      medications: [],
      dosageRules: {},
      ageGroups: {}
    },
    sourceId: 'pediatric-formulary',
    lastUpdated: new Date('2024-12-13'),
    created: new Date('2024-11-01'),
    checksum: '',
    changelog: [
      {
        version: '1.0.0',
        date: new Date('2024-11-01'),
        type: 'major',
        changes: ['Первоначальная версия протокола дозировок'],
        author: 'Система',
        description: 'Создание базового протокола расчета доз'
      },
      {
        version: '1.1.0',
        date: new Date('2024-11-15'),
        type: 'minor',
        changes: ['Добавлены новые препараты', 'Обновлены правила расчета'],
        author: 'Система',
        description: 'Расширение списка препаратов'
      },
      {
        version: '1.2.0',
        date: new Date('2024-12-13'),
        type: 'minor',
        changes: ['Добавлены цетиризин и левоцетиризин', 'Улучшена валидация доз'],
        author: 'Система',
        description: 'Добавление антигистаминных препаратов'
      }
    ],
    isActive: true
  });
};

// Инициализируем протоколы при загрузке
initializeMedicalProtocols();
